import React, { useEffect, useId, useRef, useState } from 'react';

import { IAlphaPendingConfirmation } from 'store/alpha/types';
import {
  confirmationTitle,
  formatConfirmationValue,
  humanizeConfirmationKey,
} from 'services/alpha/confirmationFormat';
import { cardMayTakeFocus } from 'components/Alpha/cardFocus';
import { CardServerError } from 'components/Alpha/cards/cardTypes';
import { ANOTHER_REQUEST_TEXT, CardFailureAlert } from 'components/Alpha/cards/useCardActions';
import useStyles from 'components/Alpha/styles';

interface IProps {
  confirmation: IAlphaPendingConfirmation;
  onConfirm: (token: string) => Promise<boolean>;
  onCancel: (token: string) => void | Promise<void>;
  blocked?: boolean;
  blockedNote?: string;
  cancelBlocked?: boolean;
  cancelling?: boolean;
  serverError?: CardServerError;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const AlphaConfirmationCard: React.FC<IProps> = ({
  confirmation,
  onConfirm,
  onCancel,
  blocked = false,
  blockedNote = ANOTHER_REQUEST_TEXT,
  cancelBlocked = false,
  cancelling = false,
  serverError,
}) => {
  const classes = useStyles();
  const blockedId = `alpha-confirm-blocked-${useId().replace(/:/g, '')}`;
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const [pendingAction, setPendingAction] = useState<'confirm' | 'cancel' | undefined>();
  const preview = isPlainObject(confirmation.preview) ? confirmation.preview : {};
  const { message, created_disabled: createdDisabled, recipient_count: recipientCount, recipients_sample: recipientsSample, ...fields } =
    preview;
  const fieldEntries = Object.entries(fields).filter(([, v]) => v !== undefined && v !== null);
  const recipientSampleNames = Array.isArray(recipientsSample)
    ? recipientsSample.filter((name): name is string => typeof name === 'string')
    : [];

  useEffect(() => {
    if (cardMayTakeFocus()) titleRef.current?.focus();
  }, []);

  const busy = pendingAction !== undefined || cancelling;

  const handleConfirm = (): void => {
    if (busy || blocked) return;
    setPendingAction('confirm');
    Promise.resolve<boolean | undefined>(onConfirm(confirmation.token))
      .then(started => started !== false)
      .catch(() => false)
      .then(started => {
        if (!started) setPendingAction(undefined);
      });
  };

  const handleCancel = (): void => {
    if (busy || cancelBlocked) return;
    setPendingAction('cancel');
    Promise.resolve(onCancel(confirmation.token)).finally(() => setPendingAction(undefined));
  };

  return (
    <div className={classes.confirmCard} role="group" aria-label="Confirm this action">
      <span ref={titleRef} tabIndex={-1} data-alpha-card-title="" className={classes.confirmLabel}>
        {confirmationTitle(confirmation.title)}
      </span>
      {createdDisabled === true && (
        <span className={classes.createdDisabledBadge}>Will be created switched off</span>
      )}
      {typeof message === 'string' && (
        <span className={classes.confirmMessage}>{message}</span>
      )}
      {typeof recipientCount === 'number' && (
        <div className={classes.recipientCount}>
          {`${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
          {recipientSampleNames.length > 0 && (
            <span className={classes.recipientSample}>{` — ${recipientSampleNames.join(', ')}`}</span>
          )}
        </div>
      )}
      {fieldEntries.length > 0 && (
        <div className={classes.confirmFieldsGrid}>
          {fieldEntries.map(([key, value]) => (
            <React.Fragment key={key}>
              <span className={classes.confirmFieldKey}>{humanizeConfirmationKey(key)}</span>
              <span className={classes.confirmFieldValue}>
                {formatConfirmationValue(value).map((line, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={index}>{line}</span>
                ))}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
      <CardFailureAlert
        failure={
          serverError && {
            message: serverError.message,
            fieldErrors: {},
            unmapped: [],
            retry: 'submit',
          }
        }
        busy={busy}
        canRetrySubmit={!blocked}
        onRetrySubmit={handleConfirm}
        onRetryCancel={handleCancel}
      />
      {(blocked || cancelBlocked) && (
        <p id={blockedId} className={classes.confirmBlocked}>
          {blockedNote}
        </p>
      )}
      <div className={classes.confirmActions}>
        <button
          type="button"
          className={classes.confirmButton}
          disabled={busy}
          aria-disabled={blocked || undefined}
          aria-describedby={blocked ? blockedId : undefined}
          onClick={handleConfirm}
        >
          {pendingAction === 'confirm' ? 'Confirming…' : 'Confirm'}
        </button>
        <button
          type="button"
          className={classes.cancelButton}
          disabled={busy}
          aria-disabled={cancelBlocked || undefined}
          aria-describedby={cancelBlocked ? blockedId : undefined}
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AlphaConfirmationCard;
